import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import Button from "@mui/material/Button";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import { type FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import type { RangeKeyDict } from "react-date-range";

import { Spacer } from "scmp-app/components/common/spacer";
import { formatPlusDatePair } from "scmp-app/components/plus/helpers";
import { useCaptureElement } from "scmp-app/lib/hooks";

import type { DefinedDateRange } from "./consts";
import { DefinedDateRanges } from "./consts";
import { getDefinedDateRange } from "./helpers";
import {
  ActionButtonContainer,
  Container,
  DefinedDateRangeButton,
  DefinedDateRangeContainer,
  SpecificDateText,
  StyledArrowDropDownIcon,
  StyledBox,
  StyledButton,
  StyledChip,
  StyledDateRange,
  StyledPopper,
} from "./styles";
import type { DatePickerState, DateRangePair } from "./types";

export type Props = {
  className?: string;
  dateRange: DateRangePair;
  defaultDateRange?: DateRangePair;
  definedDateRanges?: typeof DefinedDateRanges;
  onChange?: (range: DateRangePair) => void;
};

export const DatePicker: FunctionComponent<Props> = ({
  className,
  dateRange: [startDate, endDate],
  defaultDateRange: [defaultStartDate, defaultEndDate] = [] as unknown as DateRangePair,
  definedDateRanges = DefinedDateRanges,
  onChange = () => {},
}) => {
  const defaultState = useMemo<DatePickerState>(
    () => ({
      color: "rgba(69, 133, 255, 0.20)",
      definedDateRange: null,
      endDate: defaultEndDate,
      key: "selection",
      startDate: defaultStartDate,
    }),
    [defaultEndDate, defaultStartDate],
  );

  const { captureElement, element: anchorElement } = useCaptureElement<HTMLButtonElement>();

  const [state, setState] = useState<DatePickerState>({ ...defaultState, endDate, startDate });
  const [temporaryState, setTemporaryState] = useState<DatePickerState>(state);
  const [open, setOpen] = useState(false);

  const applyDefinedDateRange = useCallback((range: DefinedDateRange) => {
    const [startDate, endDate] = getDefinedDateRange(range);
    setTemporaryState(current => ({
      ...current,
      definedDateRange: range,
      endDate: endOfDay(endDate),
      startDate: startOfDay(startDate),
    }));
  }, []);

  const handleDateRangeChanged = useCallback((range: RangeKeyDict) => {
    setTemporaryState(current => ({
      ...current,
      ...range.selection,
      endDate: range.selection.endDate ? endOfDay(range.selection.endDate) : undefined,
      startDate: range.selection.startDate ? startOfDay(range.selection.startDate) : undefined,
    }));
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleClearAll = useCallback(() => {
    setTemporaryState(state);
  }, [state]);

  const handleReset = useCallback(() => {
    setTemporaryState(defaultState);
    setState(defaultState);
    onChange([defaultStartDate, defaultEndDate]);
  }, [defaultEndDate, defaultStartDate, defaultState, onChange]);

  const handleApply = useCallback(() => {
    setState(temporaryState);
    onChange([temporaryState.startDate, temporaryState.endDate]);
    setOpen(false);
  }, [onChange, temporaryState]);

  useEffect(() => {
    if (!open) handleClearAll();
  }, [handleClearAll, open]);

  const isShowChip = useMemo(
    () =>
      startDate?.getTime() !== defaultStartDate?.getTime() ||
      endDate?.getTime() !== defaultEndDate?.getTime(),
    [startDate, endDate, defaultStartDate, defaultEndDate],
  );

  return (
    <Container className={className}>
      {startDate && endDate && isShowChip ? (
        <StyledChip
          deleteIcon={<CloseIcon />}
          icon={<DoneIcon />}
          label={state.definedDateRange ?? formatPlusDatePair(startDate, endDate)}
          onDelete={handleReset}
        />
      ) : (
        <>
          <StyledButton
            $open={open}
            disableRipple
            endIcon={<StyledArrowDropDownIcon />}
            onClick={() => setOpen?.(current => !current)}
            ref={captureElement}
            variant="outlined"
          >
            Date Range
          </StyledButton>

          <StyledPopper
            anchorEl={anchorElement}
            disablePortal={true}
            open={open}
            placement="bottom-start"
          >
            <ClickAwayListener onClickAway={handleClose}>
              <StyledBox>
                <DefinedDateRangeContainer>
                  {definedDateRanges.map((range, index) => (
                    <DefinedDateRangeButton
                      disableRipple
                      key={index}
                      onClick={() => applyDefinedDateRange(range)}
                      variant="text"
                    >
                      {range}
                    </DefinedDateRangeButton>
                  ))}
                  <SpecificDateText>
                    Specific Dates <ArrowForwardIosIcon />
                  </SpecificDateText>
                </DefinedDateRangeContainer>

                <StyledDateRange
                  dateDisplayFormat="dd/MM/yyyy"
                  fixedHeight={true}
                  onChange={handleDateRangeChanged}
                  ranges={[temporaryState]}
                />

                <ActionButtonContainer>
                  <Button onClick={handleClearAll} variant="text">
                    Clear All
                  </Button>
                  <Spacer />
                  <Button onClick={handleClose} variant="text">
                    Cancel
                  </Button>
                  <Button onClick={handleApply} variant="text">
                    Apply
                  </Button>
                </ActionButtonContainer>
              </StyledBox>
            </ClickAwayListener>
          </StyledPopper>
        </>
      )}
    </Container>
  );
};

DatePicker.displayName = "DatePicker";
